/**
 * AssessmentPage - Pagina principal do fluxo de assessment
 * @module features/assessment/AssessmentPage
 *
 * Integra os componentes de chat para coleta de dados do usuario.
 * Gerencia o fluxo de consentimento, coleta de dados e recuperacao de sessao.
 *
 * US-004: Coleta de Dados de Identificacao
 * US-006: Conversa de Chat para Assessment (redirect)
 */

import { Toaster } from '@/components/ui/sonner';
import { ConsentDialog } from '@/features/chat/components/ConsentDialog';
import { DataCollectionIndicator } from '@/features/chat/components/DataCollectionIndicator';
import { SessionRecoveryBanner } from '@/features/chat/components/SessionRecoveryBanner';
import { getUserData, useSessionStore } from '@/features/chat/stores/sessionStore';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// =============================================================================
// TYPES
// =============================================================================

type FlowState = 'loading' | 'consent' | 'collecting' | 'declined' | 'ready';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Pagina de Assessment com fluxo de coleta de dados
 *
 * Fluxo:
 * 1. Verifica se existe sessao anterior (recuperacao)
 * 2. Se nao tiver consentimento, exibe ConsentDialog
 * 3. Se aceitar, inicia coleta de dados
 * 4. Se recusar, exibe mensagem de redirecionamento
 */
export function AssessmentPage() {
  const navigate = useNavigate();
  const [flowState, setFlowState] = useState<FlowState>('loading');
  const userData = useSessionStore(getUserData);
  const setUserField = useSessionStore((state) => state.setUserField);
  const hasAllRequiredFields = useSessionStore((state) => state.hasAllRequiredFields);

  // Verifica estado inicial baseado no consentimento
  useEffect(() => {
    // Pequeno delay para garantir que o store foi hidratado
    const timer = setTimeout(() => {
      if (userData.consentGiven) {
        setFlowState('collecting');
      } else {
        setFlowState('consent');
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [userData.consentGiven]);

  /**
   * Handler para aceitar consentimento
   */
  const handleAcceptConsent = useCallback(() => {
    setFlowState('collecting');
  }, []);

  /**
   * Handler para recusar consentimento
   */
  const handleDeclineConsent = useCallback(() => {
    setFlowState('declined');
  }, []);

  /**
   * Handler para input de formulario
   */
  const handleInputChange = useCallback(
    (field: 'name' | 'email' | 'company' | 'role') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setUserField(field, e.target.value);
    },
    [setUserField]
  );

  /**
   * Handler para iniciar o assessment - navega para a seleção de tipo
   */
  const handleStartAssessment = useCallback(() => {
    if (hasAllRequiredFields()) {
      // US-013: Redirecionar para a tela de seleção de tipo de assessment
      navigate('/assessment/selection');
    }
  }, [hasAllRequiredFields, navigate]);

  // Loading state
  if (flowState === 'loading') {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div role="status" aria-label="Carregando">
          <span className="text-muted-foreground">Carregando...</span>
        </div>
      </main>
    );
  }

  // Declined state - consentimento recusado
  if (flowState === 'declined') {
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <div
          className="max-w-md text-center space-y-4"
          role="alert"
          aria-live="polite"
          data-testid="consent-declined-message"
        >
          <h1 className="text-2xl font-bold text-foreground">Consentimento Necessario</h1>
          <p className="text-muted-foreground">
            Para realizar o diagnostico, precisamos do seu consentimento para coleta de dados
            conforme a LGPD.
          </p>
          <p className="text-sm text-muted-foreground">
            Voce pode retornar a qualquer momento para iniciar o review.
          </p>
          <a
            href="/"
            className="inline-block mt-4 text-primary hover:underline"
            aria-label="Voltar para a pagina inicial"
          >
            Voltar para o inicio
          </a>
        </div>
      </main>
    );
  }

  // Consent state - exibe dialog de consentimento
  if (flowState === 'consent') {
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <ConsentDialog
          open={true}
          onAccept={handleAcceptConsent}
          onDecline={handleDeclineConsent}
        />
      </main>
    );
  }

  // Ready state - dados coletados, redirecionar para chat
  // Este estado nao deve mais ser alcancado pois o handleStartAssessment
  // agora redireciona diretamente para /assessment/chat
  if (flowState === 'ready') {
    // Fallback: redirecionar para chat se por algum motivo chegar aqui
    navigate('/assessment/chat');
    return null;
  }

  // Collecting state - formulario de coleta de dados
  return (
    <main className="min-h-screen bg-background">
      <Toaster position="top-center" />

      {/* Banner de recuperacao de sessao */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-full px-4">
        <SessionRecoveryBanner />
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
          {/* Area principal - Formulario */}
          <section aria-label="Formulario de dados">
            <header className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Vamos comecar o seu diagnostico
              </h1>
              <p className="text-muted-foreground">
                Preencha seus dados para personalizar o review.
              </p>
            </header>

            <form
              className="space-y-6 max-w-lg"
              onSubmit={(e) => e.preventDefault()}
              aria-label="Formulario de coleta de dados"
            >
              {/* Nome */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-foreground">
                  Nome completo <span className="text-destructive">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={userData.name ?? ''}
                  onChange={handleInputChange('name')}
                  placeholder="Seu nome"
                  required
                  aria-required="true"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-name"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email profissional <span className="text-destructive">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={userData.email ?? ''}
                  onChange={handleInputChange('email')}
                  placeholder="seu@email.com"
                  required
                  aria-required="true"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-email"
                />
              </div>

              {/* Empresa */}
              <div className="space-y-2">
                <label htmlFor="company" className="text-sm font-medium text-foreground">
                  Empresa
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={userData.company ?? ''}
                  onChange={handleInputChange('company')}
                  placeholder="Nome da empresa"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-company"
                />
              </div>

              {/* Cargo */}
              <div className="space-y-2">
                <label htmlFor="role" className="text-sm font-medium text-foreground">
                  Cargo
                </label>
                <input
                  id="role"
                  name="role"
                  type="text"
                  value={userData.role ?? ''}
                  onChange={handleInputChange('role')}
                  placeholder="Seu cargo"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-role"
                />
              </div>

              {/* Botao de continuar */}
              <div className="pt-4">
                <button
                  type="button"
                  onClick={handleStartAssessment}
                  disabled={!hasAllRequiredFields()}
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="button-start-assessment"
                  aria-label="Iniciar review"
                >
                  Iniciar Review
                </button>
              </div>
            </form>
          </section>

          {/* Sidebar - Indicador de progresso */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <DataCollectionIndicator />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default AssessmentPage;
